/**
 * KYC Admin light theme — "3c Hybrid", the direction of every Roommatik
 * internal tool (engineering handbook `docs/design-system/web/hybrid-3c.md`).
 *
 * Copied from the reference implementation (`roommatik.console.ui`
 * `src/theme/lightTheme.ts`, branch `candidate`) as the handbook asks, so the
 * tokens are not retyped. Two differences, both because this app renders
 * react-admin's own <List>/<Datagrid> where Console renders its own table: the
 * RaList/RaDatagrid overrides at the foot carry the handbook §9 datagrid
 * recipe, and the Console-only `captureOverlay` token is not here.
 */

import type { CSSProperties } from 'react';
import { defaultTheme } from 'react-admin';
import { alpha, createTheme, darken, PaletteOptions, Theme } from '@mui/material/styles';
import { esES } from '@mui/material/locale';

// ============================================
// TYPE DECLARATIONS
// ============================================

/**
 * The four things a status mark can say, named by what the reader has to do
 * about it rather than by a colour. A screen picks a role; the theme decides
 * the tone. `neutral` is the one every "nothing to act on" state takes, which
 * is why green is not one of its options.
 */
export type StatusRole = 'ok' | 'attention' | 'problem' | 'neutral';

/** The three tones a status role paints with: the pill, its ink and its dot. */
export interface StatusRoleTones {
  /** Fill of the tinted pill. */
  bg: string;
  /** Label ink on that fill — measured against it, never lighter than 4.5:1. */
  text: string;
  /** The solid dot, for the places that mark a state without a pill. */
  dot: string;
}

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      radius: Record<string, string>;
      shadows: Record<string, string>;
      focusRing: string;
      /** Solid-button fill (teal-ink) — shared by MuiButton (contained) and the avatar. */
      ctaFill: string;
      ctaFillHover: string;
      userMenu: Record<string, any>;
      userAvatar: Record<string, any>;
      menu: Record<string, any>;
      text: Record<string, string>;
      table: Record<string, any>;
      status: Record<StatusRole, StatusRoleTones>;
    };
    /** react-admin's own <Sidebar> reads this to size its Drawer. */
    sidebar: {
      width: number;
      closedWidth: number;
    };
  }

  interface ThemeOptions {
    custom?: {
      radius?: Record<string, string>;
      shadows?: Record<string, string>;
      focusRing?: string;
      ctaFill?: string;
      ctaFillHover?: string;
      userMenu?: Record<string, any>;
      userAvatar?: Record<string, any>;
      menu?: Record<string, any>;
      text?: Record<string, string>;
      table?: Record<string, any>;
      status?: Record<StatusRole, StatusRoleTones>;
    };
    sidebar?: {
      width?: number;
      closedWidth?: number;
    };
  }

  // The shell's two own surfaces. Declared here so the app bar, the rail and the
  // section bar can read them as what they are: without this, each call site
  // invented its own escape (`as any` twice and a structural cast once) in a
  // project that forbids `any`.
  interface TypeBackground {
    /** App bar and section bar. White, separated from the canvas by a hairline. */
    appbar: string;
    /** The rail. A light neutral, never a brand colour. */
    menu: string;
  }

  // React-admin component overrides
  interface Components {
    RaSidebar?: { styleOverrides?: Record<string, unknown> };
    RaList?: { styleOverrides?: Record<string, unknown> };
    RaDatagrid?: { styleOverrides?: Record<string, unknown> };
    RaEdit?: { styleOverrides?: Record<string, unknown> };
    RaCreate?: { styleOverrides?: Record<string, unknown> };
  }

}

declare module '@mui/material/styles' {
  /** The one display-figure style — see `typography.figure` below. */
  interface TypographyVariants {
    figure: CSSProperties;
  }

  interface TypographyVariantsOptions {
    figure?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    /** A counted figure on a dashboard tile or a summary strip. */
    figure: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    /** Subtle inline action — ghost button. Equivalent to MUI text
     *  variant but with consistent ink-on-paper hover affordance. */
    ghost: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    /** Intense solid-accent chip — a single per-view emphasis pill. Never for
     *  repeated status tints in dense tables (those use the tinted-outline
     *  colorX styles); reserved for one standout element per screen. */
    solid: true;
  }
}

// ============================================
// DESIGN TOKENS
// ============================================

/* "3c hybrid" direction (design_handoff_console_redesign, 2026-07-16):
   engineering-tool density (Linear/Vercel — thin borders, no heavy shadows)
   with a Roommatik brand tint. Radii are ~1-2px larger than the previous
   scale for a softer feel; `card` is a new dedicated token so cards/tables/
   the KPI strip don't borrow the "medium" control radius. Chips render as
   fully-rounded pills (`chip: radius.full`) per the redesign's "pastilla"
   (tinted-pill) status treatment. */
const radius = {
  small: '6px',
  medium: '7px',
  large: '10px',
  card: '10px',
  chip: '9999px',
  full: '9999px',
};

// Warm outline system: definition comes from a thin border over a light
// same-hue fill (badge-outline look), not from a thick line. Everything
// structural — cards, table frames AND the datagrid header hairline — uses
// the lighter sand `borderCard` + a soft warm shadow: no dark lines on or
// inside surfaces (Cris, 2026-07-22). `borderRow` stays a soft in-table row
// separator. The former dark `borderStrong` chip outline is gone (see chips).
// Chips (Cris, 2026-09-26): a chip is either borderless (the `solid` accent) or carries a
// thin border in a tone slightly darker than its own fill — never a dark line around a
// light pill. Neutral chips darken their sand fill; tinted status chips use the same hue
// at a higher alpha, which reads as the tint a shade darker.
const chipFill = '#F1EDE6';
// The border is derived from the chip's TEXT colour, which every chip sets to the darker tone of
// its fill's hue (status chips: `palette.X.dark` on an `alpha(X.main, .12)` fill; neutral chips:
// ink on sand). A quarter of that tone over the fill is the fill a shade darker, whatever a
// screen chose as fill — no per-chip border colours anywhere.
const chipBorderMix = 'color-mix(in srgb, currentColor 24%, transparent)';
const chipOutlineFallback = darken(chipFill, 0.12);
const borderCard = '#E2DFD9';
// The rail's internal hairline: the rules between destination blocks and the one
// above the profile block at the foot. One constant, so the two cannot drift.
const railHairline = 'rgba(40,38,36,0.10)';
const cardShadow = '0 6px 16px -12px rgba(63,49,25,.35)';
const borderRow = '#E2DFD9';

// Solid-button fill — a mid teal-ink, distinct from Signal Teal
// (`primary.main #0BA5B7`), so filled CTAs don't clash with the teal chrome
// (rail / app-bar accents). primary.main still drives links, focus, the active
// menu item and small accents; amber/ochre stays reserved for status.
const CTA_FILL = '#117E89';
const CTA_FILL_HOVER = '#0F6C76';

/* Status hues, named once and spent twice: the MUI palette below and the
   `custom.status` role table above it. They used to be spelled a second time in
   `config/constants.ts` as a literal palette of cool-slate greys and a brighter
   green/red pair that belong to no direction, which is how the same "connected"
   dot came out one green on the fleet list and another on the device detail. */
const SUCCESS_MAIN = '#16A34A';
const SUCCESS_DARK = '#15803D';
const WARNING_MAIN = '#FB923C';
const WARNING_DARK = '#9A3412';
const ERROR_MAIN = '#FB7185';
const ERROR_DARK = '#9F1239';
/* Sand neutrals (palette.grey 100/500 and text.secondary) — the "nothing to act
   on" tones, warm like the canvas rather than the slate they replace. */
const NEUTRAL_FILL = '#EEEAE3';
const NEUTRAL_DOT = '#77726A';
const NEUTRAL_TEXT = '#63605A';

const custom = {
  radius,
  // Every shadow in this direction is warm. The three that used to sit here in
  // neutral black (`none`, `strong`, `login`) were left over from the local
  // login form SSO replaced, had no consumer anywhere in `src`, and two of them
  // carried linear gradients, which web/README.md bans in both directions.
  // "3c hybrid" drops shadows on cards/tables in favour of a 1px hairline
  // border (see MuiCard below) — `subtle` is for surfaces that genuinely float
  // above content (dialogs, popovers, menus), `medium` for the ones that float
  // higher.
  shadows: {
    subtle: '0 1px 2px rgba(80,60,25,0.04), 0 4px 16px rgba(80,60,25,0.05)',
    medium: '0 4px 12px rgba(80,60,25,0.10), 0 12px 32px rgba(80,60,25,0.08)',
  },
  /** Soft halo shown on :focus-visible across interactive chrome.
   *  Built off the brand primary at low alpha so it works on both light
   *  and dark backgrounds. Applied via MuiCssBaseline below. */
  focusRing: '0 0 0 3px rgba(11, 165, 183, 0.25)',
  ctaFill: CTA_FILL,
  ctaFillHover: CTA_FILL_HOVER,
  userMenu: {
    paper: {
      width: '280px',
      borderRadius: radius.large,
      // The account menu floats over the rail, so it takes the warm float
      // shadow rather than repeating a literal black one of its own.
      boxShadow: '0 4px 12px rgba(80,60,25,0.10), 0 12px 32px rgba(80,60,25,0.08)',
    },
  },
  userAvatar: {
    size: '40px',
    bg: '#EEEAE3',
    color: '#77726A',
    iconSize: '24px',
  },
  // "3c hybrid" micro-tones not modeled by MUI's two-tier text.primary/
  // secondary (page subtitle, table header labels, monospace IDs).
  text: {
    // Darkened from #96897A to clear AA (4.5:1) for small subtitle/label text.
    tertiary: '#746E63',
    label: '#837868',
    idMono: '#A19A8D',
    // The one monospace stack. Eleven call sites asked for 'JetBrains Mono, monospace' and two
    // more for a bare 'monospace', but the document only ever loads Inter (index.html), so every
    // identifier, key prefix and command fell back to whatever the browser calls "monospace" —
    // two stacks, no token, and a font name that was never fetched. This is the system stack, so
    // it renders the same on the machine it names without a second webfont request.
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
  },
  /* The status roles, as tones. A screen names what the state MEANS —
     `ok`, `attention`, `problem`, `neutral` — and reads the pill fill, the
     label ink and the dot from here, so one health chip cannot be a different
     green from the dot next to it.
     Measured on white: `ok` label 4.60:1 (the 8% tint is deliberately lighter
     than the 12% one that only reached 4.39:1 at the 13px chips use),
     `attention` 6.49:1, `problem` 7.00:1, `neutral` 5.23:1. The dots are
     graphics, so they answer to the 3:1 floor: 3.02:1 for `ok` on its own
     tint, and the two loud roles take their darker tone, which clears it with
     room to spare. */
  status: {
    ok: { bg: alpha(SUCCESS_MAIN, 0.08), text: SUCCESS_DARK, dot: SUCCESS_MAIN },
    attention: { bg: alpha(WARNING_MAIN, 0.15), text: WARNING_DARK, dot: WARNING_DARK },
    problem: { bg: alpha(ERROR_MAIN, 0.14), text: ERROR_DARK, dot: ERROR_DARK },
    neutral: { bg: NEUTRAL_FILL, text: NEUTRAL_TEXT, dot: NEUTRAL_DOT },
  },
  // Dense table surfaces (KPI strip, list tables) — hairline borders instead
  // of shadows, per design_handoff_console_redesign.
  table: {
    headerBg: '#FAF8F3',
    // Same sand hairline as the card borders: no dark lines inside tables.
    headerBorder: borderCard,
    // ...and the same soft row separator RaDatagrid rows use (borderRow).
    rowBorder: borderRow,
    // Single source of truth for table-header label typography: the RaDatagrid
    // override spreads it, so every header renders identically. Never restyle
    // header labels locally; consume this token.
    headCell: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.3,
      textTransform: 'none' as const,
      color: '#282624',
    },
  },
  // "3c hybrid" rail: a flush, light neutral docked sidebar (not a floating
  // grey-canvas card) — its own micro-palette, distinct from the main
  // content text scale, per design_handoff_console_redesign.
  // Light neutral rail instead of a solid branded fill: the rail is a quiet
  // surface a shade off the canvas, items read in the ink scale, and teal is
  // spent on one thing only — the active entry. What separates the rail from
  // the canvas is its right-hand border, not a block of colour.
  menu: {
    openWidth: '240px',
    closedWidth: '64px',
    // Rail background lives in `palette.background.menu` (single source of
    // truth); on a light rail the right-hand edge is what separates it from
    // the canvas, so it carries the same hairline as cards and tables.
    border: '#E2DFD9',
    // Hairline between the rail's four blocks of destinations. It replaces the old uppercase group
    // labels: with seven entries a title costs more than it clarifies, and a line groups just as
    // well without asking anyone to read it. Ink-scale now that the rail is light.
    separatorColor: railHairline,
    item: {
      borderRadius: radius.small,
      margin: '2px 0',
      // Symmetric so the icon column lines up with the section labels (both are
      // inset the same 8px). The rail container adds its own symmetric 10px on
      // each side; keep both symmetric or the active chip drifts off-centre.
      padding: '8px 8px',
      gap: '12px',
      // Label typography driven from the theme (was hard-coded 13.5px/600 in
      // CustomMenu): a touch larger, lighter by default; the active item keeps
      // weight 600 (see `active.fontWeight`) for a visible weight contrast.
      fontSize: '14px',
      lineHeight: '20px',
      // 20px label + 8px padding top and bottom: the row height is fixed here
      // so MuiMenuItem's 48px default cannot stretch it.
      minHeight: '36px',
      fontWeight: 500,
      iconSize: '18px',
      color: () => '#3C3934',
      iconColor: () => '#77726A',
      hover: {
        color: () => '#282624',
        backgroundColor: () => 'rgba(40,38,36,0.05)',
      },
      // The active entry is a WASH, not a chip: a teal tint of the rail with no
      // edge and no elevation. A white fill with a hairline ring read as a card
      // or a button sitting in the list — the wrong object. This keeps the same
      // grammar as hover (both are a tint of the surface) and ranks the two by
      // hue: an ink tint means you are pointing at it, a teal tint means you are
      // on it. The leading marker stays off (`marker: 'transparent'` —
      // CustomMenu skips the pseudo-element entirely at that value).
      //
      // A tint of the brand's Deep Teal plus a 3px bar on the leading edge — no
      // border, no elevation, so it reads as a highlighted row and not as a card
      // sitting in the list. Fill and bar are derived with `alpha()` from
      // `primary.dark`, so a change to the brand primary carries through instead
      // of leaving stale hexes here.
      //
      // The ink is darker than the fill's own hue on purpose: #084C54 over the
      // 10% tint (#DDE6E4 once composited on the rail) is 7.60:1, so the label
      // carries the state and the fill only has to hint at it. The icon keeps
      // `primary.dark` (3.87:1, and 3.0 is the bar for a glyph) so it reads a
      // step brighter than its label; Signal Teal #0BA5B7 would be 2.33:1 here
      // — too light on a tint of itself.
      active: {
        color: () => '#084C54',
        backgroundColor: (theme: Theme) => alpha(theme.palette.primary.dark, 0.1),
        fontWeight: 600,
        marker: (theme: Theme) => theme.palette.primary.dark,
        markerWidth: '3px',
        // Centred in the row: the bar is inset by this much top and bottom, and
        // only its trailing corners are rounded, since the leading edge is flush
        // with the rail.
        markerInset: '8px',
        markerRadius: '0 3px 3px 0',
        boxShadow: 'none',
        iconColor: (theme: Theme) => theme.palette.primary.dark,
        hover: {
          backgroundColor: (theme: Theme) => alpha(theme.palette.primary.dark, 0.15),
        },
      },
      closedActiveIcon: {
        backgroundColor: (theme: Theme) => alpha(theme.palette.primary.dark, 0.1),
        color: () => '#084C54',
        borderRadius: '6px',
      },
    },
    // Brand lockup at the rail top: ink + muted ink now that the surface is
    // light (it used to be white on the solid fill).
    brand: {
      color: '#0E7C8A',
      mutedColor: '#77726A',
    },
    profile: {
      nameFontSize: '13px',
      nameFontWeight: 600,
      roleFontSize: '12px',
      roleFontWeight: 400,
      // A recessed plate one step deeper than the rail, with no border: it reads
      // as a base the account sits on, not as a card lifted off the surface.
      // White plus a hairline ring is reserved for the active entry — spending
      // the same treatment here made the foot look permanently selected. Hover
      // sinks it one step further; nothing else moves.
      card: {
        bg: '#EDEAE4',
        // Ink at 8%: lighter than the rail's own group rules (10%), so the plate
        // is outlined without being fenced. 1.16:1 against the plate — present
        // when you look for it, silent when you do not.
        border: 'rgba(40,38,36,0.08)',
        hoverBg: '#E7E3DC',
        borderRadius: radius.large,
        nameColor: '#282624',
        roleColor: '#746E63',
        chevronColor: '#77726A',
        // Dark teal disc with white initials. Dark because the pale disc sat at
        // 1.01:1 against the plate — the same luminance, so the circle did not
        // read as a shape at all, only its initials did. This one is 6.13:1
        // against the plate and 7.36:1 for the initials.
        //
        // A step deeper than `primary.dark #0E7C8A`, which would only reach
        // 4.10:1 here. It is the one teal in the rail that is not the active
        // entry: an identity mark, small and circular, so it does not read as a
        // second selected row.
        avatarBg: '#0A5F69',
        avatarColor: '#FFFFFF',
        avatarSize: 32,
      },
    },
    // Rail-native scrollbar in the ink scale (the global cream one reads wrong
    // here and its 8px width eats the right padding).
    scrollbarThumb: 'rgba(40,38,36,0.18)',
    scrollbarThumbHover: 'rgba(40,38,36,0.30)',
  },
};

// ============================================
// COLOR PALETTE
// ============================================

/**
 * True Design System "Danger" token (design-system/web/classic.md, /ui-design
 * spec). Kept as a standalone constant rather than folded into
 * `palette.error.main`: this theme's `error.main` is deliberately the
 * "salmon" `#FB7185` tint used for inline field-level validation (see the
 * `danger` MuiButton variant comment below) — swapping it for `#EF4444`
 * would recolor every validation message app-wide, which is out of scope
 * for roadmap#62. Consumers that need the canonical destructive-action red
 * (e.g. a future danger-styled Chip/IconButton) should reference this
 * constant directly; rewiring the existing danger-adjacent components to
 * it is left for a follow-up issue.
 */
export const DANGER_COLOR = '#EF4444';

/**
 * One viewport tall, dynamic where the engine supports it.
 *
 * `100vh` on a mobile browser does not subtract the URL bar, so the foot of a
 * `100vh` box sits underneath it and whatever is anchored there cannot be
 * reached — in the rail that is the profile block at its foot. `100dvh` follows
 * the area actually on screen; the plain `100vh` declaration stays as the
 * fallback for engines without it, which is why this is a pair and not a single
 * value. Spread it into any box meant to be exactly one viewport tall.
 */
export const FULL_VIEWPORT_HEIGHT = {
  height: '100vh',
  '@supports (height: 100dvh)': { height: '100dvh' },
} as const;

/**
 * Minimum tappable size, in px, applied at the `sm` breakpoint and below.
 *
 * The rule is WCAG 2.5.8 "Target Size (Minimum)": every pointer target is at
 * least 44x44 CSS px. Measured on a 390px viewport the app bar's controls came
 * in at 36x32 and 32x32 and the section tabs at 39px tall, so a finger hit the
 * gaps between them.
 *
 * It is spent on padding and minimum box size only — never on a larger icon —
 * and only below `sm`, so the pinned desktop metrics of the design system
 * (icon buttons 36x32 / small 32 / large 48) are untouched. The handbook's
 * `design-system/web/hybrid-3c.md` states those desktop sizes and says nothing
 * about touch; until it carries one line on this minimum, a later sweep that
 * reads only the handbook will take these three overrides for drift.
 */
export const TOUCH_TARGET_MIN = 44;

/**
 * Minimum font size, in px, for anything typed into below the `sm` breakpoint.
 *
 * Mobile Safari zooms the viewport when a field whose text is smaller than 16px
 * takes focus, and it never zooms back out: the operator is left panning a
 * magnified form. Applied to the input element only and only below `sm`, so
 * every dense desktop field keeps its size.
 */
export const MOBILE_INPUT_MIN_FONT_SIZE = 16;

/** Field box on a desktop (hybrid-3c §7, Fields): drawn 36px tall with 14px text, the
 *  density of an ordinary back-office tool. Every text field, select and autocomplete above `sm`. */
export const FIELD_HEIGHT_DESKTOP = 36;
/** What a field answers to a click on a desktop: the 40px target floor, reached by a transparent
 *  band above and below the drawn box, the way the buttons reach it. */
export const FIELD_HIT_AREA_DESKTOP = 40;
/** Text typed into a field on a desktop. */
export const FIELD_FONT_SIZE_DESKTOP = 14;
/** Field box height below `sm`, where fields are tapped (foundations §7.2). */
export const FIELD_HEIGHT_TOUCH = 48;
/** The label above a field: the caption size (13px), the smallest text anyone reads. */
export const FIELD_LABEL_SIZE = 13;
/** MUI sizes a field's line as 1.4375em; the vertical padding centres it in the drawn box. */
const FIELD_LINE = 1.4375;
const fieldPadding = (box: number, font: number) => `${(box - font * FIELD_LINE) / 2}px`;

/** The widest a form page's card grows: past this, a line of fields reads as a table. */
export const FORM_MAX_WIDTH = 960;

/**
 * Main color palette
 *
 * CUSTOMIZATION: Change these values to match your brand colors
 *
 * `warning.main`/`success.main` intentionally match roommatik-analytics.ui
 * (Insight)'s real shipped values (#FB923C / #16A34A), not the /ui-design
 * skill's token-table mirror (Amber #F59E0B / #10B981, set here by
 * roadmap#62). Insight is the proven, shipped Classic-profile reference the
 * skill itself points to; its actual code and the skill's mirror have
 * drifted apart. Cris confirmed aligning to Insight over the mirror
 * (2026-07-16). The skill mirror is worth reconciling with Insight
 * separately — not done as part of this change.
 */
const lightPalette: PaletteOptions = {
  mode: 'light',
  // Primary brand color
  primary: {
    main: '#0BA5B7',   // --rmk-signal-teal (growth-handbook tokens v2.0.0)
    light: '#55C3D2',  // --rmk-teal
    dark: '#0E7C8A',   // --rmk-deep-teal
    contrastText: '#FFFFFF',
  },
  // Secondary color
  secondary: {
    main: '#77726A',
    light: '#A19A8D',
    dark: '#57534C',
    contrastText: '#FFFFFF',
  },
  // Background colors — "3c hybrid": a warm neutral app canvas (not Insight's
  // grey canvas) with a light neutral rail, per design_handoff_console_redesign.
  background: {
    default: '#F9F8F6',
    paper: '#FFFFFF',
    appbar: '#FFFFFF',
    // Rail surface — a light neutral a shade off the canvas, so the rail reads
    // as a quiet surface rather than a block of brand colour. Teal is spent on
    // the active entry alone; this is the single token that sets the surface.
    menu: '#F4F2EE',
  },
  // Text colors
  text: {
    primary: '#282624',
    secondary: NEUTRAL_TEXT,
    // Declared on purpose. Left undefined, MUI falls back to `rgba(0,0,0,0.38)`,
    // which composites to #9E9E9E — 2.68:1 on white and 2.62:1 on the canvas,
    // below the 3:1 floor that foundations.md section 9 calls illegible at any
    // size. The token was carrying readable text on nine surfaces, the shared
    // empty state among them, so it is pinned to the tertiary ink (5.06:1 on
    // white, 4.76:1 on the canvas) rather than left to the framework.
    disabled: '#746E63',
  },
  // Interaction surfaces. Undeclared, every hover in the application falls back
  // to MUI's neutral `rgba(0,0,0,0.04)`, a black wash under a warm system: table
  // rows, outlined buttons and ghost icon buttons all drifted cool. `chipFill`
  // is the same sand the datagrid recipe names for a row hover.
  action: {
    hover: chipFill,
    selected: alpha('#282624', 0.06),
  },
  // Status colors
  error: {
    // #FB7185 "salmon" — deliberately distinct from the Design System's
    // true Danger token (#EF4444, see DANGER_COLOR above); kept for
    // inline field-level validation tinting (roadmap#62 decision).
    main: ERROR_MAIN,
    light: '#FDA4AF',
    dark: ERROR_DARK,
    contrastText: '#FFFFFF',
  },
  warning: {
    main: WARNING_MAIN,   // Insight-aligned, see palette doc-comment above
    light: '#FDBA74',
    dark: WARNING_DARK,
    contrastText: '#334155',
  },
  info: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#1E40AF',
    contrastText: '#FFFFFF',
  },
  success: {
    main: SUCCESS_MAIN,   // Insight-aligned, see palette doc-comment above
    light: '#4ADE80',
    dark: SUCCESS_DARK,
    contrastText: '#FFFFFF',
  },
  divider: borderRow,
  grey: {
    50: '#F7F5F1',
    100: NEUTRAL_FILL,
    200: '#DFDBD1',
    300: '#CDC7BB',
    400: '#A19A8D',
    500: NEUTRAL_DOT,
    600: '#57534C',
    700: '#3C3934',
    800: '#2C2926',
    900: '#282624',
  },
};

// ============================================
// THEME CREATION
// ============================================

const baseTheme = createTheme({
  ...defaultTheme,
  palette: lightPalette,
  custom: custom,
  // react-admin's own <Sidebar> reads theme.sidebar.width/closedWidth to
  // size its Drawer's flex allocation — must match custom.menu.openWidth/
  // closedWidth exactly, or the content area overlaps/clips the menu.
  sidebar: {
    width: 240,
    closedWidth: 64,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    htmlFontSize: 16,
    // Page title (application-shell header, e.g. Fleet's "Flota") — not used
    // anywhere else in the app, so redefined to the "3c hybrid" page-title
    // spec rather than the previous generic display size.
    h1: {
      fontSize: '1.625rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.375rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.015em',
    },
    h3: { fontSize: '1.1875rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em' },
    h4: { fontSize: '1.0625rem', fontWeight: 500, lineHeight: 1.4 },
    h5: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.45 },
    h6: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.45, letterSpacing: '0.01em' },
    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.55 },
    caption: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 },
    /* The one counted figure. Dashboards had three sizes off the scale (30, 26
       and 24px) with hand-tuned negative tracking and no shared spelling of
       "line these digits up". This is h1's size at h1's weight, tabular so a
       column of figures does not shift as it refreshes, and with no tracking
       override — a number is not a headline and does not want one. */
    figure: {
      fontSize: '1.625rem',
      fontWeight: 600,
      lineHeight: 1.15,
      fontVariantNumeric: 'tabular-nums',
    },
    button: { fontWeight: 500, letterSpacing: '0.01em' },
  },
});

// ============================================
// COMPONENT OVERRIDES
// ============================================

/* The theme is built then re-wrapped with the es-ES locale so MUI's
   built-in strings (pagination, date pickers, tables, a11y labels) ship
   in formal Spanish out of the box. Consumer apps can pass a different
   locale at the createTheme call site if they need en-US, etc.
   The locale is the second arg of the trailing createTheme below. */
export const lightTheme = createTheme({
  ...baseTheme,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        /* Visible focus halo on keyboard navigation — required for
           AA accessibility ("focus state always visible"). The
           default MUI outline is replaced with a brand-tinted ring
           that works against both paper and chrome surfaces. */
        '*:focus-visible': {
          outline: 'none',
          boxShadow: custom.focusRing,
          borderRadius: radius.small,
        },
        'button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible': {
          outline: 'none',
          boxShadow: custom.focusRing,
        },
        /* The document scrollbar, in the warm ink scale of the theme rather than
           as literals in a stylesheet. The rail draws its own thinner one. */
        '::-webkit-scrollbar': { width: '8px', height: '8px' },
        '::-webkit-scrollbar-track': { background: 'transparent' },
        '::-webkit-scrollbar-thumb': { background: baseTheme.palette.grey[300], borderRadius: '4px' },
        '::-webkit-scrollbar-thumb:hover': { background: baseTheme.palette.grey[400] },
        /* Touch: the floor for anything typed into, at the element itself, below
           `sm` only (see MOBILE_INPUT_MIN_FONT_SIZE). The MuiInputBase and
           MuiOutlinedInput rules below carry the same floor for the components
           that go through them; this one is what a field outside them — or one a
           screen styles by hand — cannot opt out of. Desktop is untouched. */
        [baseTheme.breakpoints.down('sm')]: {
          'input, textarea, select': {
            fontSize: MOBILE_INPUT_MIN_FONT_SIZE,
          },
        },
      },
    },

    // App bar — white, distinct from the grey canvas below it, with a hairline
    // border for extra definition (matches Insight's CustomAppBar treatment).
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${baseTheme.palette.divider}`,
        },
      },
    },

    // Alert — a notice often carries an identifier, a path or a command, which has
    // no spaces to break at. On a phone that turned the notice into its own
    // sideways scroller: the reader had to drag the message to read the end of it,
    // inside a page that scrolls the other way. Long tokens wrap instead.
    MuiAlert: {
      styleOverrides: {
        message: {
          minWidth: 0,
          overflowWrap: 'anywhere',
          // A pill inside a notice is a label, not a control, and a label that
          // names a component with its version and the reason it was skipped is
          // longer than a phone is wide. The pill refuses to break its text and
          // caps itself at the width of the notice, so the end of every long one
          // was replaced by an ellipsis — and the notice still became a sideways
          // scroller, because a pill pushed to the width of its line keeps the
          // gap on its left. Inside a notice the text wraps and the pill grows
          // downwards instead; its single-line height is unchanged, so a pill
          // that already fits looks exactly as it did.
          '& .MuiChip-root': {
            height: 'auto',
            minHeight: 32,
            whiteSpace: 'normal',
          },
          '& .MuiChip-sizeSmall': {
            minHeight: 24,
          },
          '& .MuiChip-label': {
            overflow: 'visible',
            textOverflow: 'clip',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            paddingTop: '2px',
            paddingBottom: '2px',
          },
        },
      },
    },

    // Button styles
    MuiButton: {
      defaultProps: {
        size: 'small' as const,
        // Flat, filled buttons — no elevation/shadow (modern app look).
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 500,
          // Slightly rounded (8px), not pill — flat, compact (~38px tall).
          borderRadius: radius.medium,
          textTransform: 'none',
          minHeight: '38px',
          padding: '8px 14px',
          boxShadow: 'none',
          transition: 'background-color 0.3s ease, color 0.3s ease',
          // Touch: WCAG 2.5.8 minimum target size, below `sm` only (see
          // TOUCH_TARGET_MIN). The desktop 38px height is untouched.
          [baseTheme.breakpoints.down('sm')]: {
            minHeight: TOUCH_TARGET_MIN,
          },
          '&:hover': { boxShadow: 'none' },
        },
        // Solid CTA — dark teal-ink fill (not Signal Teal), flat, so it doesn't
        // clash with the teal chrome. Links/focus/active menu keep primary.main.
        // The CTA fill belongs to the PRIMARY contained button, not to every
        // contained button. Keyed on `contained` it also painted
        // `color="error"`, so `ConfirmDialog`'s `destructive` prop had no
        // visual effect anywhere in the application and "Retirar acceso"
        // rendered in exactly the same teal as "Guardar".
        containedPrimary: {
          backgroundColor: CTA_FILL,
          color: '#FFFFFF',
          boxShadow: 'none',
          '&:hover': { backgroundColor: CTA_FILL_HOVER, boxShadow: 'none' },
        },
        // The destructive fill. `error.main` is this theme's salmon, kept for
        // inline field tinting, and white on it measures 2.69:1 — below the 3:1
        // floor. `DANGER_COLOR #EF4444` is no better for a LABEL: white on it is
        // 3.76:1, under AA for 14px text, and the token table admits it as a
        // fill or a border, never as text. `error.dark` carries white at
        // 8.02:1, so the button reads as destructive and its label is legible.
        containedError: {
          backgroundColor: baseTheme.palette.error.dark,
          color: '#FFFFFF',
          boxShadow: 'none',
          '&:hover': { backgroundColor: darken(baseTheme.palette.error.dark, 0.12), boxShadow: 'none' },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        // Secondary button — white fill + neutral border + dark text (the single
        // "white" button style; e.g. toolbar Refresh/Export/Filters, tab toggles).
        outlined: {
          backgroundColor: '#FFFFFF',
          borderColor: baseTheme.palette.divider,
          color: baseTheme.palette.text.primary,
          '&:hover': {
            backgroundColor: baseTheme.palette.action.hover,
            borderColor: baseTheme.palette.divider,
          },
        },
      },
      // One button system: contained or outlined, per house rule 4. `createButton`
      // and `danger` were declared here and used by nothing — two more species
      // waiting to break the rule. Destructive intent is now `color="error"` on a
      // contained button (`containedError` above), which is the framework's own
      // spelling of it and needs no variant.
      variants: [
        // Subtle inline action — text-style ghost. Equivalent to
        // MUI text variant but with consistent ink-on-paper hover.
        {
          props: { variant: 'ghost' },
          style: {
            backgroundColor: 'transparent',
            color: baseTheme.palette.text.primary,
            boxShadow: 'none',
            padding: '6px 12px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: baseTheme.palette.action.hover,
            },
          },
        },
        // An outlined or text button in a status colour is its own label: the
        // salmon `error.main` (2.69:1) and `warning.main` (2.26:1) are a fill and
        // a border tone, never text. The dark ink of the same hue reads as the
        // same status and measures 8.02:1 and 7.31:1. The border keeps `main`,
        // because a boundary is not read.
        {
          props: { variant: 'outlined', color: 'error' },
          style: { backgroundColor: '#FFFFFF', color: baseTheme.palette.error.dark },
        },
        {
          props: { variant: 'outlined', color: 'warning' },
          style: { backgroundColor: '#FFFFFF', color: baseTheme.palette.warning.dark },
        },
        {
          props: { variant: 'text', color: 'error' },
          style: { color: baseTheme.palette.error.dark },
        },
        {
          props: { variant: 'text', color: 'warning' },
          style: { color: baseTheme.palette.warning.dark },
        },
      ],
    },

    // Icon button styles
    // Ghost icon button: no outline at rest, the icon itself is the affordance,
    // and the only feedback is a quiet ink wash on hover. The outlined variant
    // made every row action look like a small button and competed with the real
    // buttons in the toolbar. Sizes are unchanged (36x32, small 32, large 48).
    MuiIconButton: {
      styleOverrides: {
        root: {
          border: 'none',
          borderRadius: radius.small,
          color: baseTheme.palette.text.secondary,
          width: 36,
          height: 32,
          // Touch: WCAG 2.5.8 minimum target size, below `sm` only (see
          // TOUCH_TARGET_MIN). A minimum box, not a bigger glyph — the icon
          // keeps its size and the extra pixels become padding around it.
          [baseTheme.breakpoints.down('sm')]: {
            minWidth: TOUCH_TARGET_MIN,
            minHeight: TOUCH_TARGET_MIN,
          },
          '&:hover': {
            backgroundColor: 'rgba(40,38,36,0.05)',
            color: baseTheme.palette.text.primary,
          },
        },
        colorInherit: {
          color: baseTheme.palette.text.secondary,
          '&:hover': {
            color: baseTheme.palette.primary.main,
            backgroundColor: baseTheme.palette.action.hover,
          },
        },
        colorPrimary: {
          color: baseTheme.palette.primary.main,
          '&:hover': { backgroundColor: `${baseTheme.palette.primary.main}14` },
        },
        colorSecondary: {
          color: baseTheme.palette.secondary.main,
          '&:hover': { backgroundColor: `${baseTheme.palette.secondary.main}14` },
        },
        // An icon is a graphic and needs 3:1: the salmon `error.main` (2.69:1) and
        // `warning.main` (2.26:1) are fill tints and fail it, so an icon in a
        // status colour takes the dark ink of the same hue. react-admin's own
        // buttons collapse to a coloured icon button on a phone (delete does).
        colorError: {
          color: baseTheme.palette.error.dark,
          '&:hover': { backgroundColor: `${baseTheme.palette.error.main}14` },
        },
        colorWarning: {
          color: baseTheme.palette.warning.dark,
          '&:hover': { backgroundColor: `${baseTheme.palette.warning.main}14` },
        },
        colorInfo: {
          color: baseTheme.palette.info.main,
          '&:hover': { backgroundColor: `${baseTheme.palette.info.main}14` },
        },
        colorSuccess: {
          color: baseTheme.palette.success.main,
          '&:hover': { backgroundColor: `${baseTheme.palette.success.main}14` },
        },
        sizeLarge: { width: 48, height: 48 },
        sizeSmall: { width: 32, height: 32 },
      },
    },

    // Tabs — the shell's section bar styles its own row (SectionTabs), which
    // sizes itself for touch there; this covers every other tab strip in the
    // app (e.g. the configuration form engine's sections).
    MuiTab: {
      styleOverrides: {
        root: {
          // Sentence case, like every other label in the app: MUI's default
          // uppercase made a tab row shout over the page title it sits under.
          textTransform: 'none',
          // Touch: WCAG 2.5.8 minimum target size, below `sm` only (see
          // TOUCH_TARGET_MIN). Desktop tabs keep MUI's own height.
          [baseTheme.breakpoints.down('sm')]: {
            minHeight: TOUCH_TARGET_MIN,
          },
        },
      },
    },

    // Table pagination — the footer every list of the shared datagrid kit puts
    // under its rows (and react-admin's own list pagination, which is the same
    // component).
    MuiTablePagination: {
      styleOverrides: {
        // Touch: WCAG 2.5.8 minimum target size, below `sm` only (see
        // TOUCH_TARGET_MIN). The previous/next arrows measured 36x32 at 390px.
        // MUI builds them from `IconButton`, so the MuiIconButton minimum above
        // already reaches them today; this pins the minimum to the pagination
        // slot itself, which keeps holding if the arrows are ever supplied
        // through TablePagination's own button slots instead of IconButton.
        actions: {
          [baseTheme.breakpoints.down('sm')]: {
            '& .MuiButtonBase-root': {
              minWidth: TOUCH_TARGET_MIN,
              minHeight: TOUCH_TARGET_MIN,
            },
          },
        },
      },
    },

    // Badge
    MuiBadge: {
      defaultProps: {
        color: 'primary',
        max: 99,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      },
      styleOverrides: {
        badge: ({ theme }) => ({
          borderRadius: theme.custom?.radius?.chip ?? '4px',
          minWidth: 18,
          height: 18,
          padding: '0 4px',
          border: `2px solid ${theme.palette.common.white}`,
          backgroundColor: theme.palette.primary.dark,
          color: theme.palette.common.white,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          lineHeight: 1,
        }),
      },
    },

    // Dialog actions
    // A dialog is a floating surface of this direction, so it takes the
    // direction's radius and its warm float shadow. Without an entry here every
    // dialog in the application kept MUI's stock 4px radius and its neutral
    // black shadow, and the shell had to hand-write the missing chrome on each
    // popover it built.
    // A link rendered as a BUTTON is an action, not prose, so below `sm` it takes
    // the touch minimum like every other action. Measured on the deployed build:
    // "Volver a comprobar" came out 91x43 — one pixel short, the same shape as the
    // tab strip that needed a 45px bar to give its tabs 44. Deliberately scoped to
    // `[type="button"]`: a link inside a sentence keeps its inline box, because
    // forcing those to 44px would break text wrapping mid-paragraph, and WCAG 2.5.8
    // exempts an inline target for exactly that reason.
    MuiLink: {
      styleOverrides: {
        root: {
          [baseTheme.breakpoints.down('sm')]: {
            '&[type="button"]': {
              minHeight: TOUCH_TARGET_MIN,
              display: 'inline-flex',
              alignItems: 'center',
            },
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.card,
          border: `1px solid ${borderCard}`,
          boxShadow: custom.shadows.medium,
          backgroundImage: 'none',
        },
      },
    },

    // Menus, popovers and anything else MUI lifts off the page. `elevation`
    // shadows are MUI's own neutral ramp; this replaces them with the warm one
    // and keeps the hairline that does the actual separating in this direction.
    // Menus, popovers and anything else MUI lifts off the page. The elevation
    // ramp is MUI's own neutral black; these are the same steps in the warm one.
    // Deliberately no `defaultProps` here: flattening every Paper would also
    // flatten menus and dialogs, which are meant to float.
    MuiPaper: {
      styleOverrides: {
        elevation1: { boxShadow: custom.shadows.subtle },
        elevation2: { boxShadow: custom.shadows.subtle },
        elevation3: { boxShadow: custom.shadows.medium },
        elevation4: { boxShadow: custom.shadows.medium },
        elevation8: { boxShadow: custom.shadows.medium },
        elevation24: { boxShadow: custom.shadows.medium },
      },
    },

    // The tab strip of the direction: a 2px accent underline on the selected
    // tab. MuiTab already carried the touch minimum; the strip around it was
    // styled wherever it happened to be drawn, so every tab row in the
    // application was a little different from the next. The strip's own hairline
    // stays with whoever draws the strip — the section bar puts it on its
    // container, and a second one here would draw two.
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: '2px',
          backgroundColor: baseTheme.palette.primary.main,
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px',
          fontWeight: 500,
          minWidth: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          // Nothing repaints a button here. Two descendant rules used to live in
          // this block and both broke the one-button system from inside the most
          // seen component of the application: `.MuiButton-text` was given a
          // beige fill and a border, so a deliberately quiet "Cancelar" rendered
          // as a second solid button beside the primary; and `.MuiButton-contained`
          // was pinned to the CTA fill at descendant specificity, which beat even
          // `containedError` and left every destructive confirmation teal.
          // A dialog's buttons are the same two species as everywhere else.
          '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
        },
      },
    },

    // React Admin Sidebar — the rail's Drawer chain.
    //
    // react-admin's own <Sidebar> styles `.RaSidebar-fixed` as
    // `position: fixed; height: calc(100vh - 3em); overflow-x: hidden`, which cut
    // the rail short and clipped the profile block at its foot. Overriding that
    // from the Layout's `sx` had to be forced: both rules are one class deep,
    // and the Layout's was emitted first, so it lost on order. Declared here it is
    // appended to the very rule it replaces and wins on order alone — which is why
    // this belongs in the theme and not in the layout.
    //
    // Mobile keeps react-admin's own geometry: there the rail is a temporary
    // overlay drawer, not a flush full-height column.
    RaSidebar: {
      styleOverrides: {
        root: {
          margin: 0,
          // react-admin pulls the rail up when the window scrolls, to follow an
          // app bar that hides itself. This app bar never hides.
          '&.RaSidebar-appBarCollapsed': { marginTop: 0 },
          '& .RaSidebar-docked, & .RaSidebar-paper, & .RaSidebar-fixed': { margin: 0, padding: 0 },
          '& .RaSidebar-paper': { border: 'none', backgroundColor: 'transparent' },
          [baseTheme.breakpoints.up('sm')]: {
            ...FULL_VIEWPORT_HEIGHT,
            '& .RaSidebar-docked': { ...FULL_VIEWPORT_HEIGHT },
            '& .RaSidebar-paper': { position: 'relative', ...FULL_VIEWPORT_HEIGHT, overflow: 'visible' },
            '& .RaSidebar-fixed': {
              position: 'relative',
              ...FULL_VIEWPORT_HEIGHT,
              overflow: 'visible',
            },
          },
        },
      },
    },

    // React Admin List — the frame of the handbook §9 datagrid recipe.
    //
    // In RA v5 the DOM is `RaList-main > [RaList-actions?, RaList-content (a Card)
    // > datagrid, pagination]`: pagination is a SIBLING of the Card. So the frame
    // is drawn by the Card and continued by the pagination strip under it, which
    // makes table and pagination read as one object. With no pagination the Card
    // closes the frame on its own.
    //
    // The flex chain (from `sm` up only) is what lets a list fill the screen with
    // the table scrolling inside and the pagination pinned: it takes effect only
    // when the page gives the List a height (`ListScreen`), and is inert otherwise.
    RaList: {
      styleOverrides: {
        root: {
          [baseTheme.breakpoints.up('sm')]: {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '& .RaList-main': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
            // The filter row keeps its height: the table is what gives way. Without
            // this a tall table squeezed the row and cut the field labels above it.
            '& .RaList-actions': { flexShrink: 0 },
            '& .RaList-content': { flex: '0 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' },
          },
          '& .RaList-actions': {
            backgroundColor: 'transparent',
            minHeight: 0,
            padding: 0,
          },
          '& .RaList-content': {
            backgroundColor: baseTheme.palette.background.paper,
            border: `1px solid ${borderCard}`,
            borderRadius: radius.card,
            boxShadow: 'none',
            overflow: 'hidden',
            '&:has(+ .MuiTablePagination-root)': {
              borderBottom: 'none',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
          '& .RaList-content + .MuiTablePagination-root': {
            flexShrink: 0,
            border: `1px solid ${borderCard}`,
            borderTopColor: baseTheme.palette.divider,
            borderRadius: `0 0 ${radius.card} ${radius.card}`,
            backgroundColor: baseTheme.palette.background.default,
          },
          // On a phone the rows are cards (ResponsiveDatagrid), each with its own
          // frame, so the list frame drops away rather than boxing the boxes.
          [baseTheme.breakpoints.down('sm')]: {
            '& .RaList-content, & .RaList-content + .MuiTablePagination-root': {
              border: 'none',
              borderRadius: 0,
              backgroundColor: 'transparent',
              overflow: 'visible',
            },
          },
        },
      },
    },

    // React Admin Edit / Create — a form page is its title on the canvas and
    // one card holding the form, as wide as a form reads well. The card lets
    // its content overflow so the form's action row (FormToolbar) can stay
    // pinned to the foot of the page's scroll.
    RaEdit: {
      styleOverrides: {
        root: {
          '& .RaEdit-main': { maxWidth: FORM_MAX_WIDTH },
          '& .RaEdit-card': { overflow: 'visible' },
        },
      },
    },
    RaCreate: {
      styleOverrides: {
        root: {
          '& .RaCreate-main': { maxWidth: FORM_MAX_WIDTH },
          '& .RaCreate-card': { overflow: 'visible' },
        },
      },
    },

    // React Admin Datagrid — header, rows and hover of the §9 recipe. The frame
    // belongs to RaList above; a Datagrid rendered outside a List sits in a
    // `Card`, which carries the same frame from MuiCard.
    RaDatagrid: {
      styleOverrides: {
        root: {
          '& .RaDatagrid-tableWrapper': {
            overflowX: 'auto',
          },
          [baseTheme.breakpoints.up('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            '& .RaDatagrid-tableWrapper': { flex: 1, minHeight: 0, overflow: 'auto' },
          },
          '& .RaDatagrid-headerCell': {
            backgroundColor: custom.table.headerBg,
            borderBottom: `1px solid ${custom.table.headerBorder}`,
            padding: '8px 14px',
            position: 'sticky',
            top: 0,
            zIndex: 2,
            ...custom.table.headCell,
          },
          '& .RaDatagrid-row': {
            transition: 'background-color 0.14s ease',
            '&:hover': { backgroundColor: baseTheme.palette.action.hover },
          },
          '& .RaDatagrid-rowCell': {
            fontSize: '0.8125rem',
            padding: '8px 14px',
            borderBottom: `1px solid ${custom.table.rowBorder}`,
            color: baseTheme.palette.text.primary,
          },
          '& tbody tr:last-child .RaDatagrid-rowCell': {
            borderBottom: 'none',
          },
        },
      },
    },

    // Card — "3c hybrid": a 1px hairline border, no shadow (the previous
    // Insight-style floating-card treatment is dropped in favour of flat
    // definition via borders, per design_handoff_console_redesign).
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.common.white,
          borderRadius: radius.card,
          boxShadow: cardShadow,
          border: `1px solid ${borderCard}`,
          // No outer margin. A card carrying its own spacing means the gap between
          // two of them is decided twice: the mobile card list asks its Stack for
          // 12px and got 28 (16 of margin plus the 12), and any page that lays cards
          // out with grid or flex had this fighting its own gap. Spacing belongs to
          // whatever arranges the cards, which on every screen here is a Stack, a
          // Grid or a Box that already declares one.
        },
      },
    },

    // Accordion — the kit's card frame, declared once. Four screens each drew their own: 8px in
    // Versiones, 8px in the connector catalogue, 4px in the text rows, and MUI's default elevated
    // paper (drop shadow plus the `:before` divider) in the rollout waves. An expandable block is a
    // card like any other: one border tone, the 10px card radius and no shadow
    // (hybrid-3c §1 rule 3, §2).
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.common.white,
          border: `1px solid ${borderCard}`,
          borderRadius: radius.card,
          boxShadow: 'none',
          marginBottom: baseTheme.spacing(1.5),
          // MUI draws a 1px rule above every accordion but the first, which doubles the border
          // this one already has.
          '&:before': { display: 'none' },
          '&:first-of-type': { borderRadius: radius.card },
          '&:last-of-type': { borderRadius: radius.card },
          // Expanding must not change the block's place in the stack: the default grows the
          // vertical margin by 16px and the whole page jumps under the pointer.
          '&.Mui-expanded': {
            margin: 0,
            marginBottom: baseTheme.spacing(1.5),
          },
        },
      },
    },

    // Table cell
    MuiTableCell: {
      styleOverrides: {
        root: { padding: '12px 16px' },
      },
    },

    // Chip — "warm outline" system: neutral chips read as a tinted pill with a
    // visible warm border; coloured states are a light colour wash + a border
    // and dark text of the same hue ("colour outline") so repeated status
    // tints stay legible in dense tables without over-saturating them. The
    // `solid` variant is the one-per-view intense accent.
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.chip,
          fontWeight: 500,
          backgroundColor: chipFill,
          color: '#2C2926',
          border: `1px solid ${chipOutlineFallback}`,
          '@supports (color: color-mix(in srgb, red, blue))': { borderColor: chipBorderMix },
          // Touch: WCAG 2.5.8 minimum target size, below `sm` only (see
          // TOUCH_TARGET_MIN). A chip that does something when tapped is a
          // control, and the pinned pill is 24px tall (measured 162x24 at
          // 390px). The pill keeps its size — growing it would break the
          // density of the rows it sits in — and a transparent pseudo-element
          // centred on it carries the hit box up to the minimum instead.
          // Chips that are only labels are left alone.
          [baseTheme.breakpoints.down('sm')]: {
            '&.MuiChip-clickable, &.MuiChip-deletable': {
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                height: TOUCH_TARGET_MIN,
                transform: 'translateY(-50%)',
              },
              // The delete icon has a handler of its own, so it has to stay
              // above the extended hit box: a positioned sibling declared
              // after `::before` paints, and is hit, first.
              '& .MuiChip-deleteIcon': {
                position: 'relative',
              },
            },
          },
        },
        colorSecondary: {
          backgroundColor: chipFill,
          color: '#2C2926',
        },
        colorPrimary: {
          backgroundColor: `${baseTheme.palette.primary.main}14`,
          color: baseTheme.palette.primary.dark,
        },
        colorSuccess: {
          backgroundColor: `${baseTheme.palette.success.main}14`,
          color: baseTheme.palette.success.dark,
        },
        colorWarning: {
          backgroundColor: `${baseTheme.palette.warning.main}1F`,
          color: baseTheme.palette.warning.dark,
        },
        colorError: {
          backgroundColor: `${baseTheme.palette.error.main}14`,
          color: baseTheme.palette.error.dark,
        },
        colorInfo: {
          backgroundColor: `${baseTheme.palette.info.main}14`,
          color: baseTheme.palette.info.dark,
        },
      },
      variants: [
        // `outlined` chips have nothing inside but the surface behind them, so
        // their border is that surface a shade darker: the sand hairline.
        {
          props: { variant: 'outlined' },
          style: {
            backgroundColor: 'transparent',
            border: `1px solid ${borderCard}`,
            '@supports (color: color-mix(in srgb, red, blue))': { borderColor: chipBorderMix },
          },
        },
        // An outlined chip has no fill to carry its meaning, so its own text is
        // the colour: `error.main`/`warning.main` are 2.69:1 and 2.26:1 on the
        // canvas. The dark ink of the same hue reads the same and measures
        // 8.02:1 and 7.31:1.
        {
          props: { variant: 'outlined', color: 'error' },
          style: { color: baseTheme.palette.error.dark },
        },
        {
          props: { variant: 'outlined', color: 'warning' },
          style: { color: baseTheme.palette.warning.dark },
        },
        // Intense solid accent — one standout pill per view, never for
        // repeated table status tints.
        {
          props: { variant: 'solid' },
          style: {
            border: 'none',
            backgroundColor: baseTheme.palette.primary.main,
            color: baseTheme.palette.primary.contrastText,
            fontWeight: 500,
          },
        },
      ],
    },

    // Fields — hybrid-3c §7 "Fields", taken from the reference implementation
    // (roommatik.directory.ui): an ordinary form field. The label sits ABOVE the
    // field; white field, one thin border, the buttons' 7px radius; 36px with
    // 14px text on a desktop and 48px with 16px text below `sm`. These blocks
    // replace Material's own field (label in the border, 4px corners, 40/56px),
    // which the Console theme this file was copied from still draws. Nothing on
    // a screen overrides them.
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },

    // Select — the label is above the field, so an empty value shows its option
    // ("Todos") rather than an empty box.
    MuiSelect: {
      defaultProps: { displayEmpty: true },
      styleOverrides: {
        select: {
          display: 'flex',
          alignItems: 'center',
          minHeight: `${FIELD_FONT_SIZE_DESKTOP * FIELD_LINE}px`,
          paddingTop: fieldPadding(FIELD_HEIGHT_DESKTOP, FIELD_FONT_SIZE_DESKTOP),
          paddingBottom: fieldPadding(FIELD_HEIGHT_DESKTOP, FIELD_FONT_SIZE_DESKTOP),
          // Below `sm` the element that opens the menu fills the 48px box, so the
          // whole field answers the finger.
          [baseTheme.breakpoints.down('sm')]: {
            minHeight: `${MOBILE_INPUT_MIN_FONT_SIZE * FIELD_LINE}px`,
            paddingTop: fieldPadding(FIELD_HEIGHT_TOUCH, MOBILE_INPUT_MIN_FONT_SIZE),
            paddingBottom: fieldPadding(FIELD_HEIGHT_TOUCH, MOBILE_INPUT_MIN_FONT_SIZE),
          },
        },
        // A multiple select shows its values as chips (26px with their margin),
        // which the text padding cannot fit in the drawn box: the padding shrinks so
        // one row of chips keeps the field at its size, and more rows grow it.
        multiple: {
          '&:has(.MuiChip-root)': {
            paddingTop: `${(FIELD_HEIGHT_DESKTOP - 2 - 26) / 2}px`,
            paddingBottom: `${(FIELD_HEIGHT_DESKTOP - 2 - 26) / 2}px`,
            [baseTheme.breakpoints.down('sm')]: {
              paddingTop: `${(FIELD_HEIGHT_TOUCH - 2 - 26) / 2}px`,
              paddingBottom: `${(FIELD_HEIGHT_TOUCH - 2 - 26) / 2}px`,
            },
          },
        },
        icon: { top: 'calc(50% - 12px)' },
      },
    },

    // Switch — the tappable box of a switch is the invisible input over its thumb,
    // which measures 24px tall at the small size this app uses. Below `sm` that
    // input is grown to the WCAG 2.5.8 minimum height and re-centred over the
    // thumb, and the root stops clipping so the grown box is really tappable.
    // Nothing visible moves or changes size: the input carries no pixels of its
    // own, the thumb and the track keep their geometry, and the ripple lives
    // inside the thumb's own button, which already fits the root exactly. Width
    // is left alone so a switch cannot steal the taps of the control beside it —
    // a switch with a label is already wider than the minimum, because the label
    // toggles it too.
    MuiSwitch: {
      styleOverrides: {
        root: {
          [baseTheme.breakpoints.down('sm')]: {
            overflow: 'visible',
          },
        },
        input: {
          [baseTheme.breakpoints.down('sm')]: {
            top: '50%',
            height: TOUCH_TARGET_MIN,
            transform: 'translateY(-50%)',
          },
        },
      },
    },

    // Toggle buttons — a segmented choice is a control like any button, so below
    // `sm` each segment reaches the touch minimum (it measured 44x39 at 390px).
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          [baseTheme.breakpoints.down('sm')]: {
            minHeight: TOUCH_TARGET_MIN,
            minWidth: TOUCH_TARGET_MIN,
          },
        },
      },
    },

    // Slider — the thumb draws 20px and MUI already centres a transparent 42px
    // hit area on it (`::after`). Below `sm` that area is grown to the touch
    // minimum; the drawn thumb keeps its size.
    MuiSlider: {
      styleOverrides: {
        thumb: {
          [baseTheme.breakpoints.down('sm')]: {
            '&::after': { width: TOUCH_TARGET_MIN, height: TOUCH_TARGET_MIN },
          },
        },
      },
    },

    // Input base — one field size for the whole app, whatever the component.
    MuiInputBase: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          minHeight: `${FIELD_HEIGHT_DESKTOP}px`,
          fontSize: `${FIELD_FONT_SIZE_DESKTOP / 16}rem`,
          [baseTheme.breakpoints.down('sm')]: {
            minHeight: `${FIELD_HEIGHT_TOUCH}px`,
            fontSize: MOBILE_INPUT_MIN_FONT_SIZE,
          },
        },
        input: {
          '&.MuiInputBase-inputSizeSmall': {
            paddingTop: fieldPadding(FIELD_HEIGHT_DESKTOP, FIELD_FONT_SIZE_DESKTOP),
            paddingBottom: fieldPadding(FIELD_HEIGHT_DESKTOP, FIELD_FONT_SIZE_DESKTOP),
          },
          // The placeholder can carry a real value (a default), so it is read:
          // full opacity on the secondary ink.
          '&::placeholder': {
            opacity: 1,
            color: baseTheme.palette.text.secondary,
          },
          // Below `sm` the input itself fills the 48px box with 16px text (under
          // 16px iOS zooms the page on focus). Multiline keeps its own rows.
          [baseTheme.breakpoints.down('sm')]: {
            fontSize: MOBILE_INPUT_MIN_FONT_SIZE,
            '&.MuiInputBase-inputSizeSmall:not(.MuiInputBase-inputMultiline)': {
              paddingTop: fieldPadding(FIELD_HEIGHT_TOUCH, MOBILE_INPUT_MIN_FONT_SIZE),
              paddingBottom: fieldPadding(FIELD_HEIGHT_TOUCH, MOBILE_INPUT_MIN_FONT_SIZE),
            },
          },
        },
      },
    },

    // Input label — above the field, in the flow, never floating in the border:
    // 13px, weight 500, secondary ink, secondary on focus too. On error it takes
    // the dark red ink (`error.main` is a fill tint, 2.69:1 as text).
    MuiInputLabel: {
      defaultProps: { shrink: true, disableAnimation: true },
      styleOverrides: {
        root: {
          '&, &.MuiInputLabel-formControl, &.MuiInputLabel-shrink, &.MuiInputLabel-sizeSmall, &.MuiInputLabel-outlined': {
            position: 'relative',
            transform: 'none',
            maxWidth: '100%',
          },
          fontSize: `${FIELD_LABEL_SIZE / 16}rem`,
          fontWeight: 500,
          lineHeight: 1.4,
          marginBottom: '6px',
          color: baseTheme.palette.text.secondary,
          '&.Mui-focused': { color: baseTheme.palette.text.secondary },
          '&.Mui-error': { color: baseTheme.palette.error.dark },
        },
      },
    },

    // Outlined field — rounded like the buttons, white, one thin border that
    // darkens on hover and turns teal with the focus ring on focus, no notch.
    // A transparent band above and below the 36px box reaches the 40px target on
    // a desktop; the root is isolated so the band sits under the field's text.
    MuiOutlinedInput: {
      defaultProps: { notched: false },
      styleOverrides: {
        root: {
          isolation: 'isolate',
          borderRadius: radius.medium,
          backgroundColor: baseTheme.palette.background.paper,
          '&:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: baseTheme.palette.text.secondary,
          },
          '&.Mui-focused': { boxShadow: custom.focusRing },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: baseTheme.palette.primary.main,
            borderWidth: '1px',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: DANGER_COLOR,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${-(FIELD_HIT_AREA_DESKTOP - FIELD_HEIGHT_DESKTOP) / 2}px`,
            bottom: `${-(FIELD_HIT_AREA_DESKTOP - FIELD_HEIGHT_DESKTOP) / 2}px`,
            zIndex: -1,
          },
          [baseTheme.breakpoints.down('sm')]: { '&::before': { display: 'none' } },
        },
        notchedOutline: {
          top: 0,
          borderRadius: radius.medium,
          '& legend': { display: 'none' },
        },
      },
    },

    // Form label — the same error ink as the label above, for the
    // `FormLabel`/`FormControlLabel` shapes that do not go through `MuiInputLabel`.
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            color: baseTheme.palette.error.dark,
          },
        },
      },
    },

    // Form control
    MuiFormControl: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: { marginTop: 0, marginBottom: 0 },
      },
    },

    // Autocomplete — renders its own TextField, so it takes the same size as
    // every other field.
    MuiAutocomplete: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        inputRoot: {
          paddingTop: '4px',
          paddingBottom: '4px',
          '&.MuiInputBase-sizeSmall': {
            paddingTop: '2px',
            paddingBottom: '2px',
            '& .MuiAutocomplete-input': {
              paddingTop: `${(FIELD_HEIGHT_DESKTOP - 4 - FIELD_FONT_SIZE_DESKTOP * FIELD_LINE) / 2}px`,
              paddingBottom: `${(FIELD_HEIGHT_DESKTOP - 4 - FIELD_FONT_SIZE_DESKTOP * FIELD_LINE) / 2}px`,
            },
            [baseTheme.breakpoints.down('sm')]: {
              paddingTop: 0,
              paddingBottom: 0,
              '& .MuiAutocomplete-input': {
                paddingTop: fieldPadding(FIELD_HEIGHT_TOUCH, MOBILE_INPUT_MIN_FONT_SIZE),
                paddingBottom: fieldPadding(FIELD_HEIGHT_TOUCH, MOBILE_INPUT_MIN_FONT_SIZE),
              },
            },
          },
        },
      },
    },

    // Form helper text — the line that carries a field's validation error. Without
    // an override it inherited `error.main`, the salmon kept for inline tinting,
    // which measures 2.69:1 on white: every error message in the configuration
    // engine and in the intake form was under the illegibility floor.
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: '3px',
          marginLeft: '14px',
          marginRight: '14px',
          '&.Mui-error': { color: baseTheme.palette.error.dark },
        },
      },
    },
  },
}, esES);

export default lightTheme;
